import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { retrieveSpecificWorkQ } from "../systemData"
import { getWorkQAutoRefreshConfig } from "../workQAutoRefresh"
import { isWorkQEditInteractionActive } from "../workQEditInteraction"
import { workqueueActions } from "../../store/workqueue"

export function useWorkQAutoRefresh(workqSlug, skip) {
    const dispatch = useDispatch()
    const editSessionCount = useSelector((state) => state.workqueue.editSessionCount)
    const masterLoading = useSelector((state) => state.filter.loading)
    const orderFilters = useSelector((state) => state.filter.orderFilters)
    const searchParams = useSelector((state) => state.filter.searchParams)

    const pendingRefreshRef = useRef(false)
    const masterLoadingRef = useRef(masterLoading)

    masterLoadingRef.current = masterLoading

    const sortResetKey = orderFilters
        ? `${orderFilters.group ?? ""}.${orderFilters.label ?? ""}:${orderFilters.isDesc ? "desc" : "asc"}`
        : ""
    const searchResetKey = JSON.stringify(searchParams ?? {})

    const triggerAutoRefresh = useCallback(() => {
        dispatch(workqueueActions.setRefreshApi())
    }, [dispatch])

    const shouldDeferRefresh = useCallback(() => {
        return isWorkQEditInteractionActive()
    }, [])

    const tryRunPendingRefresh = useCallback(() => {
        if (skip || document.hidden || masterLoadingRef.current) {
            return
        }

        if (shouldDeferRefresh()) {
            return
        }

        const workq = retrieveSpecificWorkQ(workqSlug)
        const { autoRefresh } = getWorkQAutoRefreshConfig(workq)
        if (!autoRefresh) {
            pendingRefreshRef.current = false
            return
        }

        pendingRefreshRef.current = false
        triggerAutoRefresh()
    }, [skip, workqSlug, triggerAutoRefresh, shouldDeferRefresh])

    useEffect(() => {
        if (editSessionCount === 0 && pendingRefreshRef.current) {
            tryRunPendingRefresh()
        }
    }, [editSessionCount, tryRunPendingRefresh])

    useEffect(() => {
        const onVisibilityChange = () => {
            if (!document.hidden && pendingRefreshRef.current) {
                tryRunPendingRefresh()
            }
        }

        document.addEventListener("visibilitychange", onVisibilityChange)
        return () => document.removeEventListener("visibilitychange", onVisibilityChange)
    }, [tryRunPendingRefresh])

    useEffect(() => {
        if (!workqSlug || skip) {
            pendingRefreshRef.current = false
            return undefined
        }

        const workq = retrieveSpecificWorkQ(workqSlug)
        const { autoRefresh, refreshAfterMs } = getWorkQAutoRefreshConfig(workq)
        if (!autoRefresh) {
            pendingRefreshRef.current = false
            return undefined
        }

        // Sort/search changed — restart the refresh countdown from now.
        pendingRefreshRef.current = false

        const tick = () => {
            const currentWorkq = retrieveSpecificWorkQ(workqSlug)
            const { autoRefresh: stillEnabled } = getWorkQAutoRefreshConfig(currentWorkq)
            if (!stillEnabled) {
                return
            }

            if (document.hidden) {
                pendingRefreshRef.current = true
                return
            }

            if (shouldDeferRefresh()) {
                pendingRefreshRef.current = true
                return
            }

            if (masterLoadingRef.current) {
                return
            }

            pendingRefreshRef.current = false
            triggerAutoRefresh()
        }

        const intervalId = setInterval(tick, refreshAfterMs)
        const pendingPollId = setInterval(() => {
            if (pendingRefreshRef.current) {
                tryRunPendingRefresh()
            }
        }, 1000)

        return () => {
            clearInterval(intervalId)
            clearInterval(pendingPollId)
        }
    }, [workqSlug, skip, sortResetKey, searchResetKey, triggerAutoRefresh, shouldDeferRefresh, tryRunPendingRefresh])
}
